import { useCallback, useMemo } from 'react';

import { useToast } from '@contexts/Toast.context';
import { buildClearPointersPatch } from '../utils/detectionObjectsPayload';
import useCollectionConfigWrite from './useCollectionConfigWrite';

/**
 * Owns the per-object detection pointer: the saved value, the immediate write,
 * and the reset-to-defaults action.
 *
 * A pick is committed the moment the user makes it — there is no Apply step. The
 * scoping, optimism and rollback that makes that safe against the other
 * collection-config writers lives in `useCollectionConfigWrite`.
 *
 * @param {string} collectionUuid
 * @param {Array}  objects - enriched collection objects from useCollectionData
 * @param {Array}  configDetectionObjects - raw `detection_objects` from the config
 */
const useDetectionPointer = (collectionUuid, objects, configDetectionObjects) => {
	const { addToast } = useToast();

	const { save, isSaving } = useCollectionConfigWrite(collectionUuid, objects, {
		id: 'detection-pointer-error',
		title: 'Failed to save shape',
	});

	/** Persist one object's pointer. No toast — the map redrawing is the confirmation. */
	const setPointer = useCallback(
		(objectKey, pointer) => save({ [objectKey]: { style: { detection_pointer: pointer } } }),
		[save]
	);

	// Overrides come from the stored config, not the results: an object detected in
	// an earlier run still has a saved pointer to clear.
	const clearPatch = useMemo(
		() =>
			buildClearPointersPatch(
				(configDetectionObjects ?? []).filter(
					entry => entry?.style?.detection_pointer != null
				)
			),
		[configDetectionObjects]
	);

	/** Drop every override, returning all objects to their taxonomy recommendation. */
	const resetPointers = useCallback(
		() =>
			save(clearPatch, {
				onSuccess: () =>
					addToast({
						id: 'detection-pointers-reset',
						title: 'Shapes reset',
						bg: 'success',
						delay: 2500,
					}),
			}),
		[save, clearPatch, addToast]
	);

	/**
	 * Object key → effective pointer, for the map layer. Keyed on a fingerprint so
	 * its identity changes only when a pointer does: it gates the map's reconcile
	 * effect, and `objects` is a fresh array after every config refetch.
	 */
	const fingerprint = useMemo(
		() =>
			(objects ?? [])
				.map(object => `${object.key}:${object.pointer}`)
				.sort()
				.join('|'),
		[objects]
	);

	const pointerByKey = useMemo(
		() => Object.fromEntries((objects ?? []).map(object => [object.key, object.pointer])),
		// eslint-disable-next-line react-hooks/exhaustive-deps -- fingerprint stands in for `objects`
		[fingerprint]
	);

	return {
		pointerByKey,
		hasOverrides: Object.keys(clearPatch).length > 0,
		isSaving,
		setPointer,
		resetPointers,
	};
};

export default useDetectionPointer;
